import { api } from '@/lib/axios';
import { assetUrl } from '@/lib/assetUrl';
import { applyCharacterSidePreload, type PreloadedSideData } from './characterProfileSide';

interface BuffEntry {
  name?: string;
  icon?: string;
}

type BuffsPayload = PreloadedSideData['buffs'];

function escapeHtml(text: unknown): string {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function buffIconUrl(icon: unknown): string {
  if (!icon) return assetUrl('/assets/images/icon/icon_default.webp');
  const clean = String(icon).replace(/^\/+/, '');
  if (clean.indexOf('assets/') === 0) return assetUrl(`/${clean}`);
  return assetUrl(`/assets/images/icon/${clean}`);
}

export function normalizeBuffsApiPayload(raw: unknown): { buffs: BuffEntry[]; runes: BuffEntry[] } {
  if (!raw || typeof raw !== 'object') {
    return { buffs: [], runes: [] };
  }
  const body = raw as { success?: boolean; data?: { buffs?: unknown; runes?: unknown } };
  if (!body.data || typeof body.data !== 'object') {
    return { buffs: [], runes: [] };
  }
  return {
    buffs: Array.isArray(body.data.buffs) ? (body.data.buffs as BuffEntry[]) : [],
    runes: Array.isArray(body.data.runes) ? (body.data.runes as BuffEntry[]) : [],
  };
}

function buildBuffsOverlayHtml(payload: { buffs: BuffEntry[]; runes: BuffEntry[] }): string {
  const defaultIcon = assetUrl('/assets/images/icon/icon_default.webp');
  let html = '';

  html += '<div class="buffs-section">';
  html += '<h3 class="buffs-title">Active Buffs</h3>';
  html += '<div class="buffs-inline">';
  if (payload.buffs.length > 0) {
    for (const buff of payload.buffs) {
      const name = escapeHtml(buff.name || 'Buff');
      html += `
        <div class="buff-icon-wrapper" title="${name}">
          <img src="${buffIconUrl(buff.icon)}" alt="${name}" class="buff-icon"
               onerror="this.src='${defaultIcon}'">
        </div>
      `;
    }
  } else {
    html += '<p class="no-buffs">No active buffs</p>';
  }
  html += '</div></div>';

  html += '<div class="buffs-section">';
  html += '<h3 class="buffs-title">Active Runes</h3>';
  html += '<div class="buffs-inline">';
  if (payload.runes.length > 0) {
    for (const rune of payload.runes) {
      const name = escapeHtml(rune.name || 'Rune');
      html += `
        <div class="buff-icon-wrapper" title="${name}">
          <img src="${buffIconUrl(rune.icon)}" alt="${name}" class="buff-icon"
               onerror="this.src='${defaultIcon}'">
        </div>
      `;
    }
  } else {
    html += '<p class="no-buffs">No active runes</p>';
  }
  html += '</div></div>';

  return html;
}

async function resolveBuffsPayload(
  charId: number,
  getSide: () => PreloadedSideData | null,
): Promise<{ buffs: BuffEntry[]; runes: BuffEntry[] }> {
  const side = getSide();
  if (side?.buffs) {
    return normalizeBuffsApiPayload(side.buffs);
  }

  const preloaded = window.__PRELOADED_CHARACTER_BUFFS__;
  if (preloaded && Number(preloaded.charId) === charId) {
    return normalizeBuffsApiPayload(preloaded.payload);
  }

  const cached = window.ItemTooltip?.cache.get(`buffs_${charId}`);
  if (cached) {
    return normalizeBuffsApiPayload(cached);
  }

  const { data } = await api.get<BuffsPayload>(`/api/v1/character/${charId}/buffs`);
  return normalizeBuffsApiPayload(data);
}

export function seedItemTooltipCache(
  charId: number,
  profile: unknown,
  avatar: unknown | null,
  buffs: BuffsPayload,
): void {
  const tt = window.ItemTooltip;
  if (!tt) return;

  tt.baseUrl = window.__APPWEB_API_BASE__ || '/api/v1';
  tt.cache.set(`profile_${charId}`, profile);
  tt.cache.set(`buffs_${charId}`, buffs);
  if (avatar) {
    tt.cache.set(`avatar_${charId}`, avatar);
  }
}

type SwitchInventoryTabFn = (
  charId: number,
  tab: string,
  btn: HTMLElement,
) => Promise<void>;

/**
 * Tab buffs vía React/axios (mismo patrón que avatar con cache).
 * Evita depender del fetch legacy de itemTooltip.js en el CDN.
 */
export function patchSwitchInventoryTabForBuffs(
  getSide: () => PreloadedSideData | null,
): () => void {
  const existing = window.switchInventoryTab as (SwitchInventoryTabFn & { __buffsBridge?: boolean }) | undefined;
  if (existing?.__buffsBridge) {
    return () => {};
  }

  const original = window.switchInventoryTab as SwitchInventoryTabFn | undefined;
  if (!original) return () => {};

  const patched: SwitchInventoryTabFn & { __buffsBridge?: boolean } = async (charId, tab, btn) => {
    if (tab !== 'buffs') {
      return original.call(window, charId, tab, btn);
    }

    document.querySelectorAll('.inventory-tab-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');

    const leftSide = document.querySelector('.equipment-left-side') as HTMLElement | null;
    const rightSide = document.querySelector('.equipment-right-side') as HTMLElement | null;
    if (leftSide) leftSide.style.display = 'none';
    if (rightSide) rightSide.style.display = 'none';

    const characterModel = document.querySelector('.character-model-container') as HTMLElement | null;
    if (characterModel) characterModel.style.opacity = '0.1';

    const displayWrapper = document.querySelector('.character-display-wrapper');
    let buffsOverlay = document.querySelector('.buffs-absolute-overlay') as HTMLElement | null;
    if (!buffsOverlay && displayWrapper) {
      buffsOverlay = document.createElement('div');
      buffsOverlay.className = 'buffs-absolute-overlay';
      displayWrapper.appendChild(buffsOverlay);
    }

    if (!buffsOverlay) return;

    try {
      const side = getSide();
      if (side) applyCharacterSidePreload(Number(charId), side);
      const payload = await resolveBuffsPayload(Number(charId), getSide);
      buffsOverlay.innerHTML = buildBuffsOverlayHtml(payload);
      buffsOverlay.style.display = 'flex';
    } catch {
      buffsOverlay.innerHTML =
        '<p style="color: #ff6b6b; text-align: center;">Error loading buffs</p>';
      buffsOverlay.style.display = 'flex';
    }
  };

  patched.__buffsBridge = true;
  window.switchInventoryTab = patched;

  return () => {
    window.switchInventoryTab = original;
  };
}
